import "./styles.css";
import { polyfillCountryFlagEmojis } from "country-flag-emoji-polyfill";
import { createScene } from "./scene";

// Windows + some Chrome builds ship with an emoji font that's missing
// regional-indicator flag glyphs. This polyfill lazy-loads a Twemoji
// country-flag webfont ONLY on those browsers (no-op on macOS/iOS/etc).
// Must run before any flag emoji hits the DOM.
polyfillCountryFlagEmojis();
import { mountMenu, type JoinMode, type MenuHandle } from "./menu";
import { startGame, type GameHandle, type GameMode } from "./game/game";
import { startDemo, type DemoHandle } from "./game/demo";
import {
  startSpectator,
  type SpectatorHandle,
} from "./game/spectator";
import { startRunner, type RunnerHandle } from "./game/runner";
import { mountLeaderboardPage } from "./game/leaderboard-page";
import { apiLeaderboard, apiRoundReplay } from "./api";
import { getStoredHandle } from "./token";
import { courseDateAt } from "@shared/course";
import { flagEmoji, isValidHandle, normalizeHandle } from "@shared/flag";
import { claim, type Identity } from "./handle";
import { randomHandle } from "./random-handle";
import {
  isMuted,
  onMuteChange,
  setMuted,
  sfx,
  startAmbient,
  unlockAudio,
} from "./audio";

const app = document.getElementById("app")!;
const menu = document.getElementById("menu")!;
const hudTop = document.getElementById("hud-top")!;
const hudPower = document.getElementById("hud-power")!;
const hudHint = document.getElementById("hud-hint")!;
const playerHandleEl = document.getElementById("player-handle")!;
const playerFlagEl = document.getElementById("player-flag")!;

document.body.classList.add("menu-open");

// Dedicated /leaderboard route — when the URL is `/leaderboard` or
// `/leaderboard/YYYY-MM-DD` we skip the 3D scene boot entirely and
// render the standalone leaderboard page. Keeps the permalink light
// (no WebGL init, no course generation) and makes direct traffic feel
// like a real page rather than a deep-link into the game.
const LB_ROUTE =
  /^\/leaderboard(?:\/(\d{4}-\d{2}-\d{2}|streaks|hof))?\/?$/.exec(
    window.location.pathname,
  );
if (LB_ROUTE) {
  menu.remove();
  document.body.classList.remove("menu-open");
  const initialDate =
    LB_ROUTE[1] && /^\d{4}-\d{2}-\d{2}$/.test(LB_ROUTE[1]) ? LB_ROUTE[1] : null;
  mountLeaderboardPage({
    myHandle: getStoredHandle() ?? null,
    initialDate,
  });
} else {
  void bootGame();
}

async function bootGame(): Promise<void> {
const scene = createScene(app);

let game: GameHandle | null = null;
let demo: DemoHandle | null = startDemo(scene);
let spectator: SpectatorHandle | null = null;
let runner: RunnerHandle | null = null;
let currentIdentity: Identity | null = null;
let menuHandle: MenuHandle | null = null;

function ensureDemo(): void {
  if (!demo && !game && !spectator && !runner) demo = startDemo(scene);
}
function stopDemo(): void {
  demo?.dispose();
  demo = null;
}
function stopSpectator(): void {
  spectator?.dispose();
  spectator = null;
}
function stopRunner(): void {
  runner?.dispose();
  runner = null;
}

function showHud(): void {
  hudTop.hidden = false;
  hudPower.hidden = false;
  hudHint.hidden = false;
  const hudCenter = document.getElementById("hud-center");
  if (hudCenter) hudCenter.hidden = false;
  setTimeout(() => ((hudHint as HTMLElement).style.opacity = "0"), 5000);
}

function hideHud(): void {
  hudTop.hidden = true;
  hudPower.hidden = true;
  hudHint.hidden = true;
  const hudCenter = document.getElementById("hud-center");
  if (hudCenter) hudCenter.hidden = true;
}

function hideMenu(): void {
  menu.style.transition = "opacity 0.3s ease";
  menu.style.opacity = "0";
  setTimeout(() => {
    menu.style.display = "none";
    document.body.classList.remove("menu-open");
  }, 320);
}

function showMenu(): void {
  menu.style.display = "";
  menu.style.opacity = "1";
  menu.style.transition = "";
  document.body.classList.add("menu-open");
}

function setIdentityChrome(identity: Identity): void {
  playerHandleEl.textContent = identity.handle;
  playerFlagEl.textContent = identity.flag;
}

// Compact score formatter — `Intl.NumberFormat` with thousand
// separators so 4-digit scores read cleanly. Used by the runner HUD
// + game-over card.
const scoreFormatter = new Intl.NumberFormat("en-US");
function formatScore(n: number): string {
  return scoreFormatter.format(Math.round(n));
}

function launch(identity: Identity, mode: GameMode): void {
  stopDemo();
  stopRunner();
  stopSpectator();
  hideRunnerHud();
  hideRunnerGameOver();
  hideSpectatorHud();
  game?.dispose();
  setIdentityChrome(identity);
  showHud();
  hideMenu();
  game = startGame(scene, identity, mode);
}

function enterDaily(identity: Identity): void {
  launch(identity, {
    kind: "daily",
    onNameTaken: () => bounceToHandleStep(),
  });
}

// Called when the user clicks Compete. Before launching the daily round,
// quickly check whether this handle already has a score on today's
// leaderboard — rejecting early saves them from playing 9 holes just to
// learn their name is taken.
async function tryEnterDaily(identity: Identity): Promise<void> {
  try {
    // Honor the dev-only `?date=YYYY-MM-DD` override so local playtests
    // of older days check the right leaderboard snapshot.
    const urlDate = (() => {
      try {
        const p = new URLSearchParams(window.location.search);
        const d = p.get("date") ?? p.get("courseDate");
        return d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
      } catch {
        return null;
      }
    })();
    const courseDate = urlDate ?? courseDateAt(Date.now());
    const lb = await apiLeaderboard(courseDate, 500);
    const taken = lb.entries.some(
      (e) => e.handle.toLowerCase() === identity.handle.toLowerCase(),
    );
    if (taken) {
      // Bounce to the welcome card with a clear status so they can pick
      // another nickname. We stay on the mode chooser briefly so the
      // message is attached to the action they just took.
      menuHandle?.reset();
      menuHandle?.showHandleStatus?.(
        `"${identity.handle}" is already on today's leaderboard — pick another.`,
        "err",
      );
      return;
    }
  } catch {
    /* leaderboard check is best-effort; fall through to launch */
  }
  enterDaily(identity);
}

function enterEndless(identity: Identity): void {
  // Endless mode key is stable for this session only — reseeding on
  // re-entry is fine, nothing persists. The seed text also feeds the
  // per-hole biome + generator downstream.
  const rngSeed = `endless/${Math.random().toString(36).slice(2)}/${Date.now()}`;
  launch(identity, {
    kind: "endless",
    rngSeed,
    onExitToMenu: () => backToModeChooser(),
  });
}

// Bring the welcome card back up in the mode-chooser state — used by the
// in-game "🏠 back" button so the player can jump between modes without
// having to retype their handle.
function backToModeChooser(): void {
  game?.dispose();
  game = null;
  hideHud();
  showMenu();
  ensureDemo();
  if (currentIdentity) menuHandle?.showModeChooser(currentIdentity);
}

// Called when the server rejects score submission because another player
// has already posted under this handle today. Tear down the game, show
// the welcome card, and force the user back to the handle-pick step.
function bounceToHandleStep(): void {
  game?.dispose();
  game = null;
  hideHud();
  showMenu();
  ensureDemo();
  menuHandle?.reset();
  currentIdentity = null;
}

function onJoined(identity: Identity, mode: JoinMode): void {
  currentIdentity = identity;
  if (mode === "endless") enterEndless(identity);
  else if (mode === "runner") enterRunner(identity);
  else void tryEnterDaily(identity);
}

// ---------- Cosmic Runner lifecycle ----------
const runnerHud = document.getElementById("runner-hud") as HTMLElement | null;
const runnerDistanceEl = document.getElementById("runner-distance");
const runnerScoreEl = document.getElementById("runner-score");
const runnerComboPill = document.getElementById(
  "runner-combo-pill",
) as HTMLElement | null;
const runnerComboEl = document.getElementById("runner-combo");
const runnerLivesEl = document.getElementById("runner-lives");
const runnerBestEl = document.getElementById("runner-best") as HTMLElement | null;
const runnerBestNumEl = document.getElementById("runner-best-num");
const runnerLaunchHint = document.getElementById(
  "runner-launch-hint",
) as HTMLElement | null;
const runnerExitBtn = document.getElementById(
  "runner-exit-btn",
) as HTMLButtonElement | null;
const runnerGameOverRoot = document.getElementById(
  "runner-gameover",
) as HTMLElement | null;
const runnerGameOverDist = document.getElementById("runner-gameover-distance");
const runnerGameOverScore = document.getElementById("runner-gameover-score");
const runnerGameOverBest = document.getElementById("runner-gameover-best");
const runnerRetryBtn = document.getElementById(
  "runner-retry-btn",
) as HTMLButtonElement | null;
const runnerMenuBtn = document.getElementById(
  "runner-menu-btn",
) as HTMLButtonElement | null;

function showRunnerHud(): void {
  if (runnerHud) runnerHud.hidden = false;
}
function hideRunnerHud(): void {
  if (runnerHud) runnerHud.hidden = true;
}
function hideRunnerGameOver(): void {
  if (runnerGameOverRoot) runnerGameOverRoot.hidden = true;
}

function enterRunner(_identity: Identity): void {
  // Tear down anything else that was claiming the scene.
  stopSpectator();
  stopRunner();
  stopDemo();
  game?.dispose();
  game = null;
  hideHud();
  hideMenu();
  // Defensive: every other overlay should be off before runner shows.
  // Without these, switching from a leaked spectator session into
  // runner left both HUDs stacked on top of each other.
  hideSpectatorHud();
  hideRunnerGameOver();
  showRunnerHud();
  const rngSeed = `runner/${Math.random().toString(36).slice(2)}/${Date.now()}`;
  runner = startRunner(scene, {
    rngSeed,
    onStateChange({ distance, lives, score, combo, bestScore, started }) {
      if (runnerDistanceEl) {
        runnerDistanceEl.textContent = String(Math.round(distance));
      }
      if (runnerScoreEl) {
        runnerScoreEl.textContent = formatScore(score);
      }
      if (runnerComboPill && runnerComboEl) {
        if (combo > 1) {
          runnerComboEl.textContent = `×${combo}`;
          runnerComboPill.hidden = false;
        } else {
          runnerComboPill.hidden = true;
        }
      }
      if (runnerLivesEl) {
        // Repeat the heart per remaining life, fade losses to grey.
        const filled = "❤️".repeat(Math.max(0, lives));
        const ghost = "🤍".repeat(Math.max(0, 3 - lives));
        runnerLivesEl.textContent = filled + ghost;
      }
      if (runnerBestEl && runnerBestNumEl) {
        if (bestScore > 0) {
          runnerBestEl.hidden = false;
          runnerBestNumEl.textContent = formatScore(bestScore);
        } else {
          runnerBestEl.hidden = true;
        }
      }
      // Show the launch hint until the player has fired their first
      // drag-and-release; auto-hide after that.
      if (runnerLaunchHint) {
        runnerLaunchHint.hidden = started;
      }
    },
    onGameOver({ distance, score, bestScore }) {
      // Show the post-run card. We keep the runner subscriber alive so
      // the corridor stays rendered behind the card; clicking retry
      // re-enters from a clean state.
      if (runnerGameOverRoot) runnerGameOverRoot.hidden = false;
      if (runnerGameOverScore) {
        runnerGameOverScore.textContent = formatScore(score);
      }
      if (runnerGameOverDist) {
        runnerGameOverDist.textContent = String(Math.round(distance));
      }
      if (runnerGameOverBest) {
        runnerGameOverBest.textContent = formatScore(bestScore);
      }
    },
    onExitToMenu: () => exitRunnerToMenu(),
  });
}

function exitRunnerToMenu(): void {
  stopRunner();
  hideRunnerHud();
  hideRunnerGameOver();
  showMenu();
  ensureDemo();
  if (currentIdentity) menuHandle?.showModeChooser(currentIdentity);
}

runnerExitBtn?.addEventListener("click", () => exitRunnerToMenu());
runnerMenuBtn?.addEventListener("click", () => exitRunnerToMenu());
runnerRetryBtn?.addEventListener("click", () => {
  if (currentIdentity) enterRunner(currentIdentity);
});

// Vibeverse portal arrival — auto-claim a handle from the `?username`
// param (or roll a random one if missing/invalid), then route through
// the normal Compete entry path so the leaderboard collision check
// still bounces correctly.
async function enterFromPortal(rawUsername: string | null): Promise<void> {
  let candidate = rawUsername ? normalizeHandle(rawUsername) : "";
  // Portal protocol allows arbitrary unicode in `username`; clamp it
  // to our [a-z0-9_] handle rules. Anything else → randomHandle.
  if (!candidate || !isValidHandle(candidate)) {
    candidate = randomHandle();
  }
  try {
    const outcome = await claim(candidate);
    if (outcome.kind === "joined") {
      onJoined(outcome.identity, "daily");
      return;
    }
  } catch {
    /* fall through to random fallback */
  }
  // Cleaned username didn't take (server invalid / network) — try a
  // random one before giving up.
  try {
    const r = await claim(randomHandle());
    if (r.kind === "joined") {
      onJoined(r.identity, "daily");
    }
  } catch {
    /* server offline; the menu stays up so the user can interact manually */
  }
}

window.addEventListener("pagehide", () => {
  game?.dispose();
  demo?.dispose();
  spectator?.dispose();
  runner?.dispose();
});

// ESC closes the spectator overlay without dragging the user back to
// game state — same shortcut they'd reach for to dismiss any modal.
window.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && spectator) {
    e.preventDefault();
    closeSpectator();
  }
});

(() => {
  const onGesture = () => {
    unlockAudio();
    if (!isMuted()) startAmbient();
    window.removeEventListener("pointerdown", onGesture);
    window.removeEventListener("keydown", onGesture);
    window.removeEventListener("touchstart", onGesture);
  };
  window.addEventListener("pointerdown", onGesture, { passive: true });
  window.addEventListener("keydown", onGesture);
  window.addEventListener("touchstart", onGesture, { passive: true });
})();

// Mute toggle in HUD.
const muteBtn = document.getElementById("hud-mute-btn") as HTMLButtonElement | null;
const syncMuteBtn = () => {
  if (!muteBtn) return;
  muteBtn.textContent = isMuted() ? "🔇" : "🔊";
  muteBtn.title = isMuted() ? "Unmute sound" : "Mute sound";
  muteBtn.setAttribute(
    "aria-label",
    isMuted() ? "Unmute sound" : "Mute sound",
  );
};
muteBtn?.addEventListener("click", () => {
  setMuted(!isMuted());
  if (!isMuted()) {
    startAmbient();
    sfx.uiClick();
  }
});
onMuteChange(syncMuteBtn);
syncMuteBtn();

// Back-to-menu HUD button — visible at all times, lets the user step out
// of a running game to the mode chooser (or the welcome card if they
// don't have an active identity).
const backBtn = document.getElementById("hud-menu-btn");
backBtn?.addEventListener("click", () => {
  if (currentIdentity) backToModeChooser();
  else bounceToHandleStep();
});

const summaryHomeBtn = document.getElementById("summary-home");
summaryHomeBtn?.addEventListener("click", () => {
  const summaryRoot = document.getElementById("summary");
  if (summaryRoot) {
    summaryRoot.classList.remove("open");
    summaryRoot.hidden = true;
  }
  if (currentIdentity) backToModeChooser();
  else bounceToHandleStep();
});

// Spectator chrome — small overlay shown only while a replay is
// playing. Drives the watch button's lifecycle without coupling
// menu.ts to the spectator module.
const specHud = document.getElementById("spectator-hud") as HTMLElement | null;
const specHandleEl = document.getElementById("spec-handle");
const specFlagEl = document.getElementById("spec-flag");
const specHoleEl = document.getElementById("spec-hole");
const specParEl = document.getElementById("spec-par");
const specStrokesEl = document.getElementById("spec-strokes");
const specCloseBtn = document.getElementById(
  "spec-close-btn",
) as HTMLButtonElement | null;

function showSpectatorHud(): void {
  if (specHud) specHud.hidden = false;
}
function hideSpectatorHud(): void {
  if (specHud) specHud.hidden = true;
}

async function watchYesterday(handle: string, courseDate: string): Promise<void> {
  const res = await apiRoundReplay(handle, courseDate);
  if (!res.ok || res.shots.length === 0) {
    // Cheap fallback — no toast infra on the welcome screen, so log
    // and bail. Most plausible cause is a mid-round cleanup before the
    // shots flushed; the watch button stays available for retry.
    console.warn("watchYesterday: replay unavailable", res);
    return;
  }
  // Tear down everything else so the spectator owns the scene.
  // Important: dispose any existing spectator first — a rapid
  // double-click (or HMR re-mount) would otherwise stack two
  // onFrame subscribers and replay frames twice.
  stopSpectator();
  stopDemo();
  game?.dispose();
  game = null;
  hideHud();
  hideMenu();
  if (specHandleEl) specHandleEl.textContent = res.handle;
  if (specFlagEl) specFlagEl.textContent = flagEmoji(res.countryCode);
  showSpectatorHud();
  spectator = startSpectator(scene, {
    handle: res.handle,
    countryCode: res.countryCode,
    courseDate: res.courseDate,
    shots: res.shots,
    onHoleChange({ holeIndex, par, holeStrokes, totalStrokes }) {
      if (specHoleEl) specHoleEl.textContent = `${holeIndex + 1} / 9`;
      if (specParEl) specParEl.textContent = String(par);
      if (specStrokesEl) {
        specStrokesEl.textContent = `${holeStrokes} · total ${totalStrokes}`;
      }
    },
    onComplete: closeSpectator,
    onClose: () => {
      // Triggered by spectator.dispose() after we've already cleaned
      // up — no extra action needed here.
    },
  });
}

function closeSpectator(): void {
  stopSpectator();
  hideSpectatorHud();
  showMenu();
  ensureDemo();
}

specCloseBtn?.addEventListener("click", () => {
  closeSpectator();
});

menuHandle = mountMenu(menu, {
  onJoined,
  onWatchYesterday: (handle, courseDate) => {
    void watchYesterday(handle, courseDate);
  },
});

// Vibeverse portal arrivals: bypass the welcome card and auto-launch
// Compete using the username from the URL. mountMenu already detects
// `?portal=true` and skips its own autoSignIn so the two flows don't
// race. The demo keeps playing in the background until claim resolves.
{
  const params = new URLSearchParams(window.location.search);
  const fromPortal =
    params.get("portal") === "true" || params.get("portal") === "1";
  if (fromPortal) {
    void enterFromPortal(params.get("username"));
  }
}
}
